import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface OfflineAction {
  id: string;
  type: 'update_tags' | 'add_note' | 'update_name' | 'create_followup' | 'create_unit';
  payload: any;
  timestamp: number;
  retryCount: number;
}

const OFFLINE_QUEUE_KEY = 'd2d-offline-queue';
const MAX_RETRY_ATTEMPTS = 3;

export const useOfflineSync = (userId?: string) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueLength, setQueueLength] = useState(0);

  // Load queue from localStorage
  const loadQueue = useCallback((): OfflineAction[] => {
    try {
      const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading offline queue:', error);
      return [];
    }
  }, []);

  // Save queue to localStorage
  const saveQueue = useCallback((queue: OfflineAction[]) => {
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
      setQueueLength(queue.length);
    } catch (error) {
      console.error('Error saving offline queue:', error);
    }
  }, []);

  // Add action to offline queue
  const queueAction = useCallback((type: OfflineAction['type'], payload: any) => {
    const action: OfflineAction = {
      id: crypto.randomUUID(),
      type,
      payload,
      timestamp: Date.now(),
      retryCount: 0
    };

    const queue = loadQueue();
    queue.push(action);
    saveQueue(queue);

    console.log('📦 Action queued for offline sync:', action);
    
    toast({
      title: "Offline-Modus",
      description: "Änderung wird synchronisiert, sobald Verbindung besteht.",
      variant: "default"
    });
  }, [loadQueue, saveQueue]);

  // Process a single action
  const processAction = useCallback(async (action: OfflineAction): Promise<boolean> => {
    try {
      console.log('🔄 Processing offline action:', action);

      switch (action.type) {
        case 'update_tags': {
          const { unitId, tags } = action.payload;
          const { error } = await supabase
            .from('d2d_units')
            .update({
              tags,
              last_contact_at: new Date().toISOString(),
              offline_sync_pending: false
            })
            .eq('id', unitId);
          
          if (error) throw error;
          break;
        }

        case 'add_note': {
          const { unitId, note } = action.payload;
          const { error } = await supabase
            .from('d2d_notes')
            .insert({
              unit_id: unitId,
              user_id: userId,
              note,
            });
          
          if (error) throw error;
          break;
        }

        case 'update_name': {
          const { unitId, name } = action.payload;
          const { error } = await supabase
            .from('d2d_units')
            .update({
              unit_name: name,
              offline_sync_pending: false
            })
            .eq('id', unitId);
          
          if (error) throw error;
          break;
        }

        case 'create_followup': {
          const { unitId, tag, reminderDate, notes, isManual } = action.payload;
          const { error } = await supabase.rpc('create_unit_follow_up', {
            unit_id_param: unitId,
            tag_param: tag,
            reminder_date_param: reminderDate,
            follow_up_notes_param: notes || null,
            is_manual_param: isManual || false
          });
          
          if (error) throw error;
          break;
        }

        case 'create_unit': {
          const { houseId, unitData } = action.payload;
          const { error } = await supabase
            .from('d2d_units')
            .insert({
              house_id: houseId,
              user_id: userId,
              organization_id: unitData.organization_id,
              unit_name: unitData.unit_name,
              tags: unitData.tags,
              offline_sync_pending: false
            });
          
          if (error) throw error;
          break;
        }

        default:
          console.warn('Unknown action type:', action.type);
          return true; // Remove unknown actions
      }

      console.log('✅ Successfully processed offline action:', action.id);
      return true;
    } catch (error) {
      console.error('❌ Error processing offline action:', error);
      return false;
    }
  }, [userId]);

  // Sync all queued actions
  const syncQueue = useCallback(async () => {
    if (isSyncing || !isOnline || !userId) return;

    const queue = loadQueue();
    if (queue.length === 0) return;

    setIsSyncing(true);
    console.log('🔄 Starting offline sync, queue length:', queue.length);

    let remainingQueue: OfflineAction[] = [];
    let successCount = 0;
    let failedCount = 0;

    for (const action of queue) {
      const success = await processAction(action);
      
      if (success) {
        successCount++;
      } else {
        // Increment retry count
        action.retryCount++;
        
        if (action.retryCount < MAX_RETRY_ATTEMPTS) {
          remainingQueue.push(action);
        } else {
          console.error('❌ Action exceeded max retry attempts:', action);
          failedCount++;
        }
      }
    }

    saveQueue(remainingQueue);
    setIsSyncing(false);

    console.log('✅ Offline sync completed', { successCount, failedCount, remaining: remainingQueue.length });

    if (successCount > 0) {
      toast({
        title: "Synchronisierung abgeschlossen",
        description: `${successCount} Änderung(en) erfolgreich synchronisiert.`,
        variant: "default"
      });
    }

    if (failedCount > 0) {
      toast({
        title: "Synchronisierungsfehler",
        description: `${failedCount} Änderung(en) konnten nicht synchronisiert werden.`,
        variant: "destructive"
      });
    }
  }, [isSyncing, isOnline, userId, loadQueue, saveQueue, processAction]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Connection restored');
      setIsOnline(true);
      toast({
        title: "Verbindung wiederhergestellt",
        description: "Synchronisiere Offline-Änderungen...",
        variant: "default"
      });
    };

    const handleOffline = () => {
      console.log('📴 Connection lost');
      setIsOnline(false);
      toast({
        title: "Verbindung unterbrochen",
        description: "Änderungen werden lokal gespeichert.",
        variant: "default"
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && userId) {
      // Small delay to ensure connection is stable
      const timer = setTimeout(() => {
        syncQueue();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isOnline, userId, syncQueue]);

  // Update queue length on mount
  useEffect(() => {
    const queue = loadQueue();
    setQueueLength(queue.length);
  }, [loadQueue]);

  return {
    isOnline,
    isSyncing,
    queueLength,
    queueAction,
    syncQueue
  };
};
